import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/hashes/utils';

/**
 * Canonical JSON — keys sorted, no whitespace.
 * Required for deterministic content hashes across machines.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value !== null && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[key] = sortKeys((value as Record<string, unknown>)[key]);
    }
    return sorted;
  }
  return value;
}

/**
 * Content identifier: `sha256:<hex>` of the canonical JSON encoding.
 * Deterministic across machines and stable under key reordering.
 */
export function cid(value: unknown): string {
  const canonical = canonicalJson(value);
  const hash = sha256(utf8ToBytes(canonical));
  return `sha256:${bytesToHex(hash)}`;
}

/**
 * Ed25519 signer used to sign Proof Ledger entries.
 *
 * Keys are stored as raw 32-byte private + 32-byte public. Persistence,
 * rotation, and hardware-backing are concerns of higher layers.
 */
export class LedgerSigner {
  private constructor(
    private readonly privateKey: Uint8Array,
    readonly publicKey: Uint8Array,
  ) {}

  /** Generate a fresh random keypair. */
  static generate(): LedgerSigner {
    const priv = ed25519.utils.randomPrivateKey();
    const pub = ed25519.getPublicKey(priv);
    return new LedgerSigner(priv, pub);
  }

  /** Rehydrate a signer from a 32-byte hex private key. */
  static fromPrivateKeyHex(privHex: string): LedgerSigner {
    const priv = hexToBytes(privHex.startsWith('0x') ? privHex.slice(2) : privHex);
    const pub = ed25519.getPublicKey(priv);
    return new LedgerSigner(priv, pub);
  }

  /** Hex-encode the public key. */
  publicKeyHex(): string {
    return bytesToHex(this.publicKey);
  }

  /** Hex-encode the private key. Handle with care. */
  privateKeyHex(): string {
    return bytesToHex(this.privateKey);
  }

  /** Sign an arbitrary message; returns hex-encoded signature. */
  sign(message: string): string {
    const sig = ed25519.sign(utf8ToBytes(message), this.privateKey);
    return bytesToHex(sig);
  }

  /**
   * Verify a signature given the message, hex-signature, and hex public key.
   * Never throws — returns false on malformed input.
   */
  static verify(message: string, signatureHex: string, publicKeyHex: string): boolean {
    try {
      return ed25519.verify(hexToBytes(signatureHex), utf8ToBytes(message), hexToBytes(publicKeyHex));
    } catch {
      return false;
    }
  }
}
